import { useCallback, useEffect, useMemo, useState } from 'react';
import { BeltRank } from '@/types/belts';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollableCurriculumTabStrip } from '@/components/ui/scrollable-curriculum-tab-strip';
import {
  Tabs,
  TabsContent,
  TabsTrigger,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { BreakingContentModel } from './CurriculumNonCombatTheory';
import { BreakingTabBody } from './CurriculumNonCombatTheory';

interface SparringProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  /** Board breaking requirements (curriculum: last sub-tab). */
  breaking: BreakingContentModel;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  /**
   * `curriculum` = tabbed sub-layout for the main curriculum Sparring tab.
   * `legacy` (default) = original stacked sections + weapon accordions (accordion dropdown).
   */
  presentation?: 'curriculum' | 'legacy';
  /** Belt-colored pill styling for curriculum sub-tabs only. */
  beltTabSurfaceClass?: string;
}

interface WeaponRequirement {
  name: string;
  description: string | JSX.Element;
}

interface WeaponsSection {
  requirements: string;
  options?: string[];
  description?: string;
}

interface SparringRequirements {
  unarmed: {
    type: string;
    description: string;
  };
  weapons?: WeaponRequirement[];
  weaponsSection?: WeaponsSection;
}

type MainTab = 'empty-hand' | 'weapons-training' | 'advanced-weapons' | 'breaking';

const DEFAULT_TAB_SURFACE = '!bg-gray-200 !text-gray-900';

/** Ordered weapon slots → tab label (matches curriculum names). */
const WEAPON_SLOTS: {
  value: string;
  label: string;
  matches: (w: WeaponRequirement) => boolean;
}[] = [
  { value: 'arnis-12', label: 'Arnis 12 Strikes', matches: (w) => w.name === 'Arnis 12 Strikes' },
  {
    value: 'west-coast',
    label: 'West Coast Arnis',
    matches: (w) => w.name.startsWith('West Coast Arnis'),
  },
  {
    value: '12-strike-defences',
    label: '12 Strike Defences',
    matches: (w) => w.name === 'Defences against the 12 strikes',
  },
  {
    value: 'sinwali',
    label: 'Sinwali with Partner',
    matches: (w) => w.name === 'Sinawali with Partner',
  },
];

function getWeaponDemoAliases(weapon: WeaponRequirement): string[] | null {
  if (weapon.name === 'Arnis 12 Strikes') {
    return ['arnis_12_strikes', 'arnis12', 'arnis 12 strikes'];
  }
  if (weapon.name.startsWith('West Coast Arnis')) {
    return ['wc_arnis_form', 'west_coast_arnis', 'west coast arnis'];
  }
  if (weapon.name === 'Sinawali with Partner') {
    return ['siniwali', 'sinawali'];
  }
  return null;
}

function resolveWeaponTabs(weapons: WeaponRequirement[]) {
  return WEAPON_SLOTS.map((slot) => {
    const weapon = weapons.find((w) => slot.matches(w));
    if (!weapon) return null;
    return { value: slot.value, label: slot.label, weapon };
  }).filter(
    (x): x is { value: string; label: string; weapon: WeaponRequirement } => x !== null,
  );
}

function buildMainTabs(reqs: SparringRequirements): { value: MainTab; label: string }[] {
  const tabs: { value: MainTab; label: string }[] = [
    { value: 'empty-hand', label: 'Empty Hand' },
  ];
  if (reqs.weapons && reqs.weapons.length > 0) {
    tabs.push({ value: 'weapons-training', label: 'Weapons Training' });
  }
  if (reqs.weaponsSection) {
    tabs.push({ value: 'advanced-weapons', label: 'Advanced Weapons' });
  }
  tabs.push({ value: 'breaking', label: 'Breaking' });
  return tabs;
}

// Get sparring requirements for each belt
const getSparringRequirements = (belt: BeltRank): SparringRequirements => {
  const arnis12StrikesContent = {
    name: 'Arnis 12 Strikes',
    description: (
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-base mb-2 text-taekwondo-red">12 Angles of Attack</h4>
          <p className="text-gray-700 mb-2">
            Stick is held in right hand for all strikes + step right foot forward for each strike
          </p>
          <ul className="space-y-2">
            <li>
              <span className="text-taekwondo-red font-semibold">1.</span> Strike diagonally from right shoulder to left hip
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">2.</span> Strike diagonally from left shoulder to right hip
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">3.</span> Strike Horizontally right to left
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">4.</span> Strike Horizontally left to right
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">5.</span> Straight forward thrust to Mid Section
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">6.</span> Strike opponents left collarbone area with end of stick
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">7.</span> Strike opponents right collarbone area with end of stick
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">8.</span> Back hand Strike to Right Knee
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">9.</span> Forehand Strike to left knee
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">10.</span> Strike opponents right cheekbone/eye area with end of stick
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">11.</span> Strike opponents left cheekbone/eye area with end of stick
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">12.</span> Strike straight down to top of opponent's head
            </li>
          </ul>
        </div>
      </div>
    ),
  };

  const getWestCoastArnisContent = (b: BeltRank) => {
    const isBlackWhite = b === 'BLACK/WHITE';

    return {
      name: 'West Coast Arnis - A creative form from Ernie Reyes Snr',
      description: (
        <div className="space-y-4">
          <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">
              A creative form from Ernie Reyes Snr.
          </h3>
          <ul className="space-y-2">
            <li>
              <span className="text-taekwondo-red font-semibold">1.</span> Start in attention (sticks by side)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">2.</span> Rotate sticks backwards (once) and step left leg out whilst crossing sticks at the same time (head height)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">3.</span> Cross-sticks down and out, tension
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">4.</span> Step right foot forward into back stance, and with sticks strike like a double forearm block
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">5.</span> Step right foot 90 degrees right into a front stance (keep head facing in the previous direction) adopt the ready position—left arm across body, right arm by the side of head
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">6.</span> Step right foot up into natural stance –4 redonda strikes (3 strike)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">7.</span> Step back into ready position as before
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">8.</span> Step right foot (90 degrees) forward and 4 strikes—two to the neck and two to the arm (As you strike make sure you move the sticks first i.e. if you strike to the neck with the right, move the left out of the way first. Each time you strike both sticks should be on the same side.)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">9.</span> Step back (right leg) into set position—Back stance with sticks slightly crossed at low section
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">10.</span> Step forward into front stance and lunge with right stick (Left stick should be covering the arm)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">11.</span> Bring the left stick under the right arm while executing a backfist strike with stick. Rotate anti-clockwise, then clockwise above the head. Strike downwards towards the knees
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">12.</span> Step right leg back into back stance and spread sticks parallel
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">13.</span> Step left foot over into front stance. Strike with the right and then step over, turning 180 degrees into set position
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">14.</span> Sidekick mid-section
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">15.</span> Turn 180 degrees spread sticks in back stance
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">16.</span> Step left foot into front stance and reverse lunge with stick (Left arm covering right arm)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">17.</span> Right leg front kick
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">18.</span> One step jump front kick (Right leg) As you land step left behind right (X-stance)
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">19.</span> Cross-sticks downwards
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">20.</span> Step right leg out into start position, as at the beginning
            </li>
            <li>
              <span className="text-taekwondo-red font-semibold">21.</span> 2 redonda strikes (3 strike) and step back into start position. (Half way Point)
            </li>
            {!isBlackWhite ? (
              <>
                <li>
                  <span className="text-taekwondo-red font-semibold">22.</span> Pivot on your right foot 180 degrees anti clock wise land at diagonal, into left front stance and strike to the head with the left stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">23.</span> Step forward into right front stance and strike to the head then the knee with the right stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">24.</span> Step the right foot over to the right diagonal into front stance and strike to the head with the right stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">25.</span> Step forward into left front stance and strike to the head then the knee with the left stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">26.</span> Step the left foot over to the left (back to centre line) adopt the ready position—left arm across body, right arm by the side of head
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">27.</span> Sinawali to the left side of body execute a right leg front kick re chamber leg and execute a side kick to 45 degree angle land kicking leg in front (right front stance)
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">28.</span> Sinawali to the right side of body execute a left leg front kick re chamber leg and execute a side kick to 45 degree angle land kicking leg in front (left front stance)
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">29.</span> Sinawali then adjust into a left back stance spread sticks
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">30.</span> Strike with the right stick while and turn anti clock wise 270 degrees into a left front stance and strike to the head with the left stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">31.</span> Step forward into a right front stance and strike to the head with the right stick Rotate anti-clockwise, then clockwise above the head. Strike downwards towards the knees with the right stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">32.</span> Turn 180 degrees clock wise into a right front stance and strike to the head with the right stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">33.</span> Step forward into left front stance and strike to the head with the left stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">34.</span> Step back into a right front stance and strike to the head with the right stick Rotate anti-clockwise, then clockwise above the head. Strike downwards towards the knees with the right stick
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">35.</span> Step right foot back to left (face front) execute 2 redonda strikes (3 strike)
                </li>
                <li>
                  <span className="text-taekwondo-red font-semibold">36.</span> Step right foot forward circle both arms forward over the head and strike to floor step back to ready stance with both sticks crossed over head bring both sticks down and out to sides, tension
                </li>
              </>
            ) : (
              <li>
                <span className="text-taekwondo-red font-semibold">22.</span> Step right foot forward circle both arms forward over the head and strike to floor step back to ready stance with both sticks crossed over head bring both sticks down and out to sides, tension
              </li>
            )}
          </ul>
        </div>
      ),
    };
  };

  switch (belt) {
    case 'WHITE':
    case 'YELLOW':
    case 'ORANGE':
    case 'BLUE':
    case 'GREEN':
    case 'PURPLE':
    case 'BROWN':
    case 'RED':
      return {
        unarmed: {
          type: getUnarmedType(belt),
          description: getUnarmedDescription(belt),
        },
      };
    case 'NAVY':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 2 rounds x 1 minute',
        },
        weapons: [arnis12StrikesContent],
      };
    case 'BLACK/WHITE':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 2 rounds x 1.5 minutes',
        },
        weapons: [
          arnis12StrikesContent,
          getWestCoastArnisContent(belt),
          {
            name: 'Defences against the 12 strikes',
            description: 'Details to be confirmed',
          },
        ],
      };
    case 'BLACK/D1':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 3 rounds x 1 minutes',
        },
        weapons: [
          arnis12StrikesContent,
          getWestCoastArnisContent(belt),
          {
            name: 'Defences against the 12 strikes',
            description: 'Details to be confirmed',
          },
          {
            name: 'Sinawali with Partner',
            description:
              'Perform the moves against a partner with arnis sticks - hitting each others sticks',
          },
        ],
      };
    case 'BLACK/D2':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 3 rounds x 1 minutes',
        },
        weapons: [
          arnis12StrikesContent,
          getWestCoastArnisContent(belt),
          {
            name: 'Defences against the 12 strikes',
            description: 'Details to be confirmed',
          },
          {
            name: 'Sinawali with Partner',
            description:
              'Perform the moves against a partner with arnis sticks - hitting each others sticks',
          },
        ],
        weaponsSection: {
          requirements: 'Choose 2 out of the following 3:',
          options: [
            'Pal Bahng Moo Gong',
            'Creative Pattern',
            'Sukwon (Bo-Staff form - Calling of the soul)',
          ],
        },
      };
    case 'BLACK/D3':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 3 rounds x 1 minutes',
        },
        weapons: [
          arnis12StrikesContent,
          getWestCoastArnisContent(belt),
          {
            name: 'Defences against the 12 strikes',
            description: 'Details to be confirmed',
          },
          {
            name: 'Sinawali with Partner',
            description:
              'Perform the moves against a partner with arnis sticks - hitting each others sticks',
          },
        ],
        weaponsSection: {
          requirements: 'Required:',
          description: 'Sukwon (Bo-Staff form)',
          options: [
            'Choose one: Pal Bahng Moo Gong OR Creative Weapon form',
            'Creative open hand pattern (required)',
          ],
        },
      };
    case 'BLACK/D4':
    case 'BLACK/D5':
      return {
        unarmed: {
          type: 'WT Contact Sparring',
          description: 'WT contact sparring with pads 3 rounds x 1 minutes',
        },
        weapons: [
          arnis12StrikesContent,
          getWestCoastArnisContent(belt),
          {
            name: 'Defences against the 12 strikes',
            description: 'Details to be confirmed',
          },
          {
            name: 'Sinawali with Partner',
            description:
              'Perform the moves against a partner with arnis sticks - hitting each others sticks',
          },
        ],
        weaponsSection: {
          requirements: 'Required:',
          options: [
            'Pal Bahng Moo Gong 1-8',
            'Sukwon (Bo-Staff form)',
            'Creative Weapon form',
            'Creative open hand pattern',
          ],
        },
      };
    default:
      return {
        unarmed: {
          type: 'No Sparring',
          description: 'No sparring requirements',
        },
      };
  }
};

// Helper functions for unarmed requirements
const getUnarmedType = (belt: BeltRank): string => {
  switch (belt) {
    case 'WHITE':
      return 'Shadow Sparring';
    case 'YELLOW':
      return '3 for 3 Kick Sparring';
    case 'ORANGE':
    case 'BLUE':
      return 'Non-Contact Sparring';
    case 'GREEN':
    case 'PURPLE':
    case 'BROWN':
    case 'RED':
      return 'WT Contact Sparring';
    default:
      return 'No Sparring';
  }
};

const getUnarmedDescription = (belt: BeltRank): string => {
  switch (belt) {
    case 'WHITE':
      return 'Shadow sparring 2-3 rounds';
    case 'YELLOW':
      return 'Non-contact 3 for 3 kick sparring 2-3 rounds';
    case 'ORANGE':
    case 'BLUE':
      return 'Non-contact sparring 2-3 rounds';
    case 'GREEN':
    case 'PURPLE':
    case 'BROWN':
    case 'RED':
      return 'WT contact sparring with pads 2 rounds x 1 minute';
    default:
      return 'No sparring requirements';
  }
};

function WeaponTrainingBody({
  weapons,
  beltTabSurfaceClass,
  hasDemo,
  onWatchDemo,
}: {
  weapons: WeaponRequirement[];
  beltTabSurfaceClass: string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
}) {
  const weaponTabs = useMemo(() => resolveWeaponTabs(weapons), [weapons]);
  const [activeWeaponTab, setActiveWeaponTab] = useState(() => weaponTabs[0]?.value ?? 'arnis-12');

  useEffect(() => {
    setActiveWeaponTab((prev) =>
      weaponTabs.some((t) => t.value === prev) ? prev : weaponTabs[0]?.value ?? 'arnis-12',
    );
  }, [weaponTabs]);

  const renderWeaponPanel = useCallback(
    (weapon: WeaponRequirement) => {
      const aliases = getWeaponDemoAliases(weapon);
      return (
        <div className="space-y-3 pt-2">
          {aliases && hasDemo?.(aliases) && (
            <button
              type="button"
              className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
              onClick={() => onWatchDemo?.(aliases)}
            >
              Watch Demonstration Video
            </button>
          )}
          <div className="text-base text-gray-700">{weapon.description}</div>
        </div>
      );
    },
    [hasDemo, onWatchDemo],
  );

  if (weaponTabs.length === 0) {
    return (
      <p className="text-sm text-gray-600">No weapons requirements for this belt.</p>
    );
  }

  return (
    <Tabs
      value={activeWeaponTab}
      onValueChange={setActiveWeaponTab}
      className="w-full"
    >
      <ScrollableCurriculumTabStrip
        key={weaponTabs.map((t) => t.value).join('-')}
        ariaLabelPrefix="Sparring weapons training"
      >
        {weaponTabs.map((t) => (
          <TabsTrigger
            key={t.value}
            value={t.value}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium',
              'border border-black/10 transition-shadow',
              beltTabSurfaceClass,
              'data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/35 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100',
            )}
          >
            {t.label}
          </TabsTrigger>
        ))}
      </ScrollableCurriculumTabStrip>
      {weaponTabs.map((t) => (
        <TabsContent key={t.value} value={t.value} className="mt-3 focus-visible:outline-none">
          <div className="rounded-lg bg-gray-50 p-4">{renderWeaponPanel(t.weapon)}</div>
        </TabsContent>
      ))}
    </Tabs>
  );
}

function SparringLegacyLayout({
  sparringReqs,
  hasDemo,
  onWatchDemo,
  selectedBelt,
  breaking,
}: {
  sparringReqs: SparringRequirements;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  selectedBelt: BeltRank;
  breaking: BreakingContentModel;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">Empty Hand</h3>
        <div className="rounded-lg bg-gray-50 p-4">
          <h4 className="mb-2 text-base font-medium text-taekwondo-red">{sparringReqs.unarmed.type}</h4>
          <p className="text-base text-gray-700">{sparringReqs.unarmed.description}</p>
        </div>
      </div>

      {sparringReqs.weapons && (
        <div>
          <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">Weapons Training</h3>
          <Accordion type="single" collapsible className="space-y-2">
            {sparringReqs.weapons.map((weapon, index) => {
              let aliases: string[] | null = null;

              if (weapon.name === 'Arnis 12 Strikes') {
                aliases = ['arnis_12_strikes', 'arnis12', 'arnis 12 strikes'];
              } else if (weapon.name.startsWith('West Coast Arnis')) {
                aliases = ['wc_arnis_form', 'west_coast_arnis', 'west coast arnis'];
              } else if (weapon.name === 'Sinawali with Partner') {
                aliases = ['siniwali', 'sinawali'];
              }

              return (
                <AccordionItem key={index} value={`weapon-${index}`}>
                  <AccordionTrigger className="rounded-lg bg-gray-50 py-2 text-base hover:bg-gray-100 hover:no-underline">
                    {weapon.name}
                  </AccordionTrigger>
                  <AccordionContent className="px-0">
                    <div className="mt-2 space-y-3 rounded-lg bg-gray-50 p-4">
                      {aliases && hasDemo?.(aliases) && (
                        <button
                          type="button"
                          className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                          onClick={() => onWatchDemo?.(aliases!)}
                        >
                          Watch Demonstration Video
                        </button>
                      )}
                      <div className="text-base text-gray-700">{weapon.description}</div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}

      {sparringReqs.weaponsSection && (
        <div>
          <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">Advanced Weapons Forms</h3>
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 font-medium text-base text-taekwondo-red">
              {sparringReqs.weaponsSection.requirements}
            </h4>
            {sparringReqs.weaponsSection.description && (
              <p className="mb-2 text-base text-gray-700">{sparringReqs.weaponsSection.description}</p>
            )}
            <ul className="list-inside list-disc space-y-1">
              {sparringReqs.weaponsSection.options?.map((option, index) => (
                <li key={index} className="text-gray-700">
                  {option}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div>
        <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">Breaking</h3>
        <BreakingTabBody selectedBelt={selectedBelt} breaking={breaking} />
      </div>
    </div>
  );
}

export default function Sparring({
  selectedBelt,
  getBeltColor: _getBeltColor,
  breaking,
  hasDemo,
  onWatchDemo,
  presentation = 'legacy',
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: SparringProps) {
  const sparringReqs = useMemo(
    () => getSparringRequirements(selectedBelt),
    [selectedBelt],
  );

  if (presentation === 'legacy') {
    return (
      <SparringLegacyLayout
        sparringReqs={sparringReqs}
        hasDemo={hasDemo}
        onWatchDemo={onWatchDemo}
        selectedBelt={selectedBelt}
        breaking={breaking}
      />
    );
  }

  const mainTabs = useMemo(() => buildMainTabs(sparringReqs), [sparringReqs]);

  const [activeMainTab, setActiveMainTab] = useState<MainTab>(() => mainTabs[0]?.value ?? 'empty-hand');

  useEffect(() => {
    setActiveMainTab((prev) =>
      mainTabs.some((t) => t.value === prev) ? prev : mainTabs[0]?.value ?? 'empty-hand',
    );
  }, [mainTabs, selectedBelt]);

  const surface = beltTabSurfaceClass;

  return (
    <div className="space-y-4 text-left">
      <Tabs value={activeMainTab} onValueChange={(v) => setActiveMainTab(v as MainTab)} className="w-full">
        <ScrollableCurriculumTabStrip
          key={mainTabs.map((t) => t.value).join('-')}
          ariaLabelPrefix="Sparring and breaking"
        >
          {mainTabs.map((t) => (
            <TabsTrigger
              key={t.value}
              value={t.value}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium',
                'border border-black/10 transition-shadow',
                surface,
                'data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/35 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100',
              )}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>

        <TabsContent value="empty-hand" className="mt-4 focus-visible:outline-none">
          <div className="rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 text-base font-medium text-taekwondo-red">{sparringReqs.unarmed.type}</h4>
            <p className="text-base text-gray-700">{sparringReqs.unarmed.description}</p>
          </div>
        </TabsContent>

        {sparringReqs.weapons && sparringReqs.weapons.length > 0 && (
          <TabsContent value="weapons-training" className="mt-4 focus-visible:outline-none">
            <WeaponTrainingBody
              weapons={sparringReqs.weapons}
              beltTabSurfaceClass={surface}
              hasDemo={hasDemo}
              onWatchDemo={onWatchDemo}
            />
          </TabsContent>
        )}

        {sparringReqs.weaponsSection && (
          <TabsContent value="advanced-weapons" className="mt-4 focus-visible:outline-none">
            <div className="rounded-lg bg-gray-50 p-4">
              <h4 className="mb-2 font-medium text-base text-taekwondo-red">
                {sparringReqs.weaponsSection.requirements}
              </h4>
              {sparringReqs.weaponsSection.description && (
                <p className="mb-2 text-base text-gray-700">{sparringReqs.weaponsSection.description}</p>
              )}
              <ul className="list-inside list-disc space-y-1">
                {sparringReqs.weaponsSection.options?.map((option, index) => (
                  <li key={index} className="text-gray-700">
                    {option}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        )}

        <TabsContent value="breaking" className="mt-4 focus-visible:outline-none">
          <BreakingTabBody selectedBelt={selectedBelt} breaking={breaking} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
